import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './AgentReport.css';

const updateOptions = [
    'Player Signed for a Club',
    'Player Received Trials Offer',
    'Player Signed Official Contract with the Agency',
    'Player Signed a Sponsorship Deal',
    'Player Transferred to a New Club',
    'Other',
];

const AgentReport = () => {
    const location = useLocation();
    const [agentId, setAgentId] = useState(null);
    const [activeTab, setActiveTab] = useState('report');
    const [submittedReports, setSubmittedReports] = useState([]);

    const [form, setForm] = useState({
        updateType: '',
        generalExplanation: '',
        playerProfileLink: '',
        followUps: {},
        files: null,
    });

    useEffect(() => {
        const idFromLocation = location.state?.userId;
        const idFromStorage = localStorage.getItem('agentId');
        const resolvedId = idFromLocation || idFromStorage;
        setAgentId(resolvedId);
    }, [location.state]);

    useEffect(() => {
        if (activeTab === 'submitted' && agentId) {
            fetchReports();
        }
    }, [activeTab, agentId]);

    const fetchReports = async () => {
        try {
            const res = await fetch(`http://localhost:5000/api/agent-report?agentId=${agentId}`);
            const data = await res.json();
            if (res.ok) {
                setSubmittedReports(data);
            } else {
                console.error('Failed to fetch submitted reports:', data.message);
            }
        } catch (err) {
            console.error('Fetch error:', err);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleFollowUpChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({
            ...prev,
            followUps: { ...prev.followUps, [name]: value },
        }));
    };

    const handleFileChange = (e) => {
        setForm((prev) => ({ ...prev, files: e.target.files }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!agentId) {
            alert('Agent ID missing. Cannot submit report.');
            return;
        }

        const formData = new FormData();
        formData.append('agentId', agentId);
        formData.append('updateType', form.updateType);
        formData.append('generalExplanation', form.generalExplanation);
        formData.append('playerProfileLink', form.playerProfileLink);
        formData.append('followUps', JSON.stringify(form.followUps));

        if (form.files) {
            for (let i = 0; i < form.files.length; i++) {
                formData.append('files', form.files[i]);
            }
        }

        try {
            const res = await fetch('http://localhost:5000/api/agent-report', {
                method: 'POST',
                body: formData,
            });

            const data = await res.json();
            if (res.ok) {
                alert('Report submitted successfully!');
                setForm({
                    updateType: '',
                    generalExplanation: '',
                    playerProfileLink: '',
                    followUps: {},
                    files: null,
                });
            } else {
                alert(`Error: ${data.message || 'Submission failed'}`);
            }
        } catch (err) {
            console.error('Submission error:', err);
            alert('Server error. Please try again later.');
        }
    };

    const renderFollowUpQuestions = () => {
        const q = [];

        switch (form.updateType) {
            case 'Player Signed for a Club':
                q.push(
                    { label: 'Club Name', name: 'clubName' },
                    { label: 'City, Country', name: 'location' },
                    { label: 'Contract Duration', name: 'contractDuration' },
                    { label: 'Transfer Fee (if any)', name: 'transferFee' },
                    { label: 'Did Your Agency Negotiate the Deal?', name: 'agencyInvolved' }
                );
                break;
            case 'Player Received Trials Offer':
                q.push(
                    { label: 'Club Name', name: 'trialClubName' },
                    { label: 'City, Country', name: 'trialLocation' },
                    { label: 'Trial Dates', name: 'trialDates' },
                    { label: 'Will Player Attend?', name: 'playerWillAttend' }
                );
                break;
            case 'Player Signed Official Contract with the Agency':
                q.push(
                    { label: 'Contract Duration', name: 'agencyContractDuration' },
                    { label: 'Exclusive Representation?', name: 'exclusive' },
                    { label: 'Signed Digitally or Physically?', name: 'signatureType' }
                );
                break;
            case 'Player Signed a Sponsorship Deal':
                q.push(
                    { label: 'Sponsor Name', name: 'sponsorName' },
                    { label: 'Duration & Scope', name: 'sponsorshipDetails' },
                    { label: 'Brokered by You?', name: 'brokeredByYou' }
                );
                break;
            case 'Player Transferred to a New Club':
                q.push(
                    { label: 'New Club Name', name: 'newClubName' },
                    { label: 'Previous Club', name: 'previousClub' },
                    { label: 'Transfer Type (Permanent/Loan)', name: 'transferType' },
                    { label: 'Transfer Fee (if any)', name: 'transferFee' },
                    { label: 'Did You Handle the Negotiation?', name: 'handledNegotiation' }
                );
                break;
            case 'Other':
                q.push(
                    { label: 'Describe the Update in Detail', name: 'otherDescription' },
                    { label: 'Who Was Involved?', name: 'partiesInvolved' },
                    { label: 'Outcome or Result', name: 'result' }
                );
                break;
            default:
                return null;
        }

        return q.map(({ label, name }) => (
            <div key={name} className="agent-reporting-form-group">
                <label>{label}:</label>
                <input
                    type="text"
                    name={name}
                    value={form.followUps[name] || ''}
                    onChange={handleFollowUpChange}
                />
            </div>
        ));
    };

    const formatLabel = (key) =>
        key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());

    return (
        <div className="agent-reporting-wrapper">
            <div className="top-header">
                <h1 className="agent-page-title">Agent Reporting</h1>
                <div className="toggle-buttons">
                    <button
                        className={activeTab === 'report' ? 'active-tab' : ''}
                        onClick={() => setActiveTab('report')}
                    >
                        Agent Reporting
                    </button>
                    <button
                        className={activeTab === 'submitted' ? 'active-tab' : ''}
                        onClick={() => setActiveTab('submitted')}
                    >
                        Submitted Reports
                    </button>
                </div>
            </div>

            {activeTab === 'report' && (
                <div className="agent-reporting-container">
                    <h2 className="agent-reporting-title">Agent Reporting</h2>
                    <p className="agent-reporting-subtitle">
                        This is where you report the career progress of the player you acquired through VSN.
                    </p>

                    <form onSubmit={handleSubmit} className="agent-reporting-form">
                        <div className="agent-reporting-form-group">
                            <label>Type of Update:</label>
                            <select
                                name="updateType"
                                value={form.updateType}
                                onChange={handleChange}
                                required
                            >
                                <option value="">-- Select Update Type --</option>
                                {updateOptions.map((opt, idx) => (
                                    <option key={idx} value={opt}>
                                        {opt}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="agent-reporting-form-group">
                            <label>General Explanation:</label>
                            <textarea
                                name="generalExplanation"
                                value={form.generalExplanation}
                                onChange={handleChange}
                                rows="4"
                                placeholder="Explain what happened..."
                                required
                            />
                        </div>

                        <div className="agent-reporting-form-group">
                            <label>VSN Player Profile Link of the Player You are Updating us:</label>
                            <input
                                type="text"
                                name="playerProfileLink"
                                value={form.playerProfileLink}
                                onChange={handleChange}
                                placeholder="https://virtualscoutingnetwork.com/player-profile/..."
                                required
                            />
                        </div>

                        {form.updateType && (
                            <div className="agent-reporting-follow-up-section">
                                <h4 className="agent-reporting-follow-up-title">Follow-up Questions</h4>
                                {renderFollowUpQuestions()}
                            </div>
                        )}

                        <div className="agent-reporting-form-group">
                            <label>Upload Supporting Documents:</label>
                            <input type="file" multiple onChange={handleFileChange} />
                        </div>

                        <button type="submit" className="agent-reporting-submit-button">
                            Submit Report
                        </button>
                    </form>
                </div>
            )}

            {activeTab === 'submitted' && (
                <div className="submitted-reports-cards">
                    {submittedReports.length === 0 ? (
                        <p>No reports submitted yet.</p>
                    ) : (
                        submittedReports.map((report, idx) => (
                            <div key={idx} className="submitted-report-card">
                                <h3 className="submitted-title">{report.updateType}</h3>
                                <p><strong>Explanation:</strong> {report.generalExplanation}</p>
                                {report.playerProfileLink && (
                                    <p><strong>Player Profile:</strong>{' '}
                                        <a
                                            href={report.playerProfileLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {report.playerProfileLink}
                                        </a>
                                    </p>
                                )}

                                {report.followUps && typeof report.followUps === 'object' && (
                                    <div className="followup-section">
                                        <h4>Follow-up Details:</h4>
                                        <ul>
                                            {Object.entries(report.followUps).map(([q, a], i) => (
                                                <li key={i}>
                                                    <strong>{formatLabel(q)}:</strong> {a}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {report.fileUrls?.length > 0 && (
                                    <div className="file-section">
                                        <h4>Attachments:</h4>
                                        <ul>
                                            {report.fileUrls.map((url, i) => (
                                                <li key={i}>
                                                    <a
                                                        href={`http://localhost:5000${url}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {url.split('/').pop()}
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default AgentReport;



















